import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { extractDocumentId } from '@/lib/documents/extract-document-id';
import { isNative } from '@/lib/platform';

/** Resolved routing for a clicked editor link. */
export type EditorLinkTarget =
  | { kind: 'document'; id: string }
  | { kind: 'anchor'; anchor: string }
  | { kind: 'external'; url: string };

/** Protocols we allow to open externally. javascript:, data:, etc. are blocked. */
const SAFE_EXTERNAL = /^(https?:|mailto:|tel:)/i;

/**
 * Classify a link href into a navigation target — pure, no DOM/runtime deps.
 *
 *  - `nula://document/<uuid>` or a bare UUID → in-app document navigation
 *  - `#anchor` → scroll to heading
 *  - http(s)/mailto/tel → external open
 *  - anything else (empty, about:blank, javascript:, unknown scheme) → null
 *
 * Mirrors the legacy Lexical LinkClickPlugin's getLinkType + extractDocumentId.
 */
export function resolveEditorLink(href: string): EditorLinkTarget | null {
  const h = href?.trim();
  if (!h || h === 'about:blank') return null;

  if (h.startsWith('#')) return { kind: 'anchor', anchor: h.slice(1) };

  const docId = extractDocumentId(h);
  if (docId) return { kind: 'document', id: docId };

  if (SAFE_EXTERNAL.test(h)) return { kind: 'external', url: h };
  return null; // blocked / unknown protocol
}

/** Link-type tag for styling (matches globals.css `a[data-link-type='…']`). */
export type LinkType = 'document' | 'anchor' | 'external';

/**
 * Classify a link href for the `data-link-type` styling attribute. Documents
 * (nula://document/<id> or bare UUID) and anchors get their own icon via CSS;
 * everything else is `external`. Looser than resolveEditorLink (which gates
 * navigation) — styling never returns null, it just defaults to `external`.
 */
export function linkTypeOf(href: string): LinkType {
  const h = href?.trim() ?? '';
  if (h.startsWith('#')) return 'anchor';
  if (extractDocumentId(h)) return 'document';
  return 'external';
}

function openExternal(url: string): void {
  if (isNative) {
    void import('@tauri-apps/plugin-opener').then(({ openUrl }) => openUrl(url));
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

export interface LinkClickOptions {
  /** Navigate to a document entity (opens it as a tab). Injected by the host. */
  onNavigate: (documentId: string) => void;
  /** Scroll the editor to a heading anchor. Optional. */
  onAnchor?: (anchor: string) => void;
}

const linkClickKey = new PluginKey('linkClick');

/**
 * Intercepts clicks on link marks and routes them:
 *   - document links (`nula://document/<id>` / bare UUID) → onNavigate(id)
 *   - external (http/https/mailto/tel) → open in a new tab / OS browser
 *   - #anchors → onAnchor (host scrolls)
 *
 * Ports the legacy Lexical LinkClickPlugin to Tiptap. Uses a ProseMirror
 * handleClickOn plugin (robust — reads the link mark from the document model,
 * not a fragile React DOM listener). The editor's Link extension must allow the
 * `nula://` protocol so the href survives to the click (see rich-text-editor).
 */
export const LinkClickExtension = Extension.create<LinkClickOptions>({
  name: 'linkClick',

  addOptions() {
    return { onNavigate: () => {}, onAnchor: undefined };
  },

  addProseMirrorPlugins() {
    const { onNavigate, onAnchor } = this.options;

    return [
      new Plugin({
        key: linkClickKey,
        props: {
          handleClickOn(view: EditorView, pos, _node, _nodePos, event) {
            const linkMark = view.state.doc
              .resolve(pos)
              .marks()
              .find((m) => m.type.name === 'link');
            const href = linkMark?.attrs.href as string | undefined;
            if (!href) return false; // not a link — let ProseMirror handle it

            const target = resolveEditorLink(href);
            if (!target) return false;

            event.preventDefault();
            switch (target.kind) {
              case 'document':
                onNavigate(target.id);
                return true;
              case 'anchor':
                onAnchor?.(target.anchor);
                return true;
              case 'external':
                openExternal(target.url);
                return true;
            }
          },
        },
      }),
    ];
  },
});
